<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description VehicleInventory
-->
<template>
  <div class="inventory">
    <van-nav-bar title="车辆盘点" left-arrow @click-left="$router.back()" />

    <!-- 搜索栏 -->
    <div class="search-bar">
      <van-search
        v-model="searchText"
        placeholder="搜索车架号/公司"
        shape="round"
      />
    </div>

    <!-- 待盘点列表 -->
    <div class="vehicle-list">
      <div
        class="vehicle-card"
        v-for="item in filteredList"
        :key="item.no"
        @click="startCheck(item)"
      >
        <div class="vehicle-info">
          <div class="vehicle-vid">{{ item.vid }}</div>
          <div class="vehicle-detail">
            {{ item.vehicleBand || "" }} {{ item.vehicleModel || "" }}
          </div>
          <div class="vehicle-company">
            {{ item.ownerCompany || "未知公司" }}
          </div>
          <div class="vehicle-location">
            地点：{{ item.storageLocation || "-" }}
          </div>
        </div>
        <div class="vehicle-action" v-if="canCheck">
          <van-button type="primary" size="small">盘点</van-button>
        </div>
      </div>

      <div v-if="!loading && taskList.length === 0" class="empty-tip">
        暂无待盘点任务
      </div>
    </div>

    <!-- 盘点弹窗 -->
    <van-dialog
      v-model="showCheckDialog"
      title="车辆盘点"
      show-cancel-button
      :before-close="beforeDialogClose"
    >
      <div class="check-content">
        <div class="check-info">
          <div class="info-row">
            <span class="label">车架号：</span>
            <span>{{ currentTask.vid }}</span>
          </div>
          <div class="info-row">
            <span class="label">车辆：</span>
            <span
              >{{ currentTask.vehicleBand }}
              {{ currentTask.vehicleModel }}</span
            >
          </div>
          <div class="info-row">
            <span class="label">公司：</span>
            <span>{{ currentTask.ownerCompany || "未知" }}</span>
          </div>
        </div>

        <!-- 视频上传 -->
        <div class="video-upload">
          <div class="upload-tip">请拍摄车架号视频（不超过100MB）</div>
          <input
            type="file"
            ref="videoInput"
            accept="video/*"
            capture="environment"
            @change="onVideoSelected"
            style="display: none"
          />
          <van-button
            type="primary"
            block
            @click="$refs.videoInput.click()"
            v-if="!selectedVideo"
          >
            选择/录制视频
          </van-button>
          <div v-else class="video-preview">
            <div class="video-name">{{ selectedVideo.name }}</div>
            <div class="video-size">{{ formatSize(selectedVideo.size) }}</div>
            <van-button plain type="danger" size="small" @click="clearVideo">
              重新选择
            </van-button>
          </div>
        </div>

        <!-- 照片上传 -->
        <div class="photo-upload">
          <div class="upload-tip">拍摄车辆照片（最多9张，每张不超过20MB）</div>
          <input
            type="file"
            ref="photoInput"
            accept="image/*"
            capture="environment"
            multiple
            @change="onPhotosSelected"
            style="display: none"
          />
          <van-button
            type="primary"
            plain
            block
            @click="$refs.photoInput.click()"
          >
            选择/拍摄照片
          </van-button>
          <div class="photo-preview-list" v-if="selectedPhotos.length > 0">
            <div
              class="photo-preview-item"
              v-for="(photo, index) in selectedPhotos"
              :key="index"
            >
              <img :src="photo.url" class="photo-thumb" />
              <div class="photo-remove" @click="removePhoto(index)">×</div>
            </div>
          </div>
          <div class="photo-count" v-if="selectedPhotos.length > 0">
            已选择 {{ selectedPhotos.length }} / 9 张照片
          </div>
        </div>

        <!-- 备注 -->
        <van-field
          v-model="checkRemark"
          type="textarea"
          placeholder="备注（选填）"
          rows="2"
          maxlength="200"
          show-word-limit
        />

        <!-- 定位信息 -->
        <div class="location-info">
          <div class="location-label">定位信息：</div>
          <div class="location-value">{{ locationName }}</div>
          <div class="location-coord" v-if="latitude">
            经纬度：{{ latitude.toFixed(6) }}, {{ longitude.toFixed(6) }}
          </div>
          <div class="location-accuracy" v-if="locationAccuracy">
            精度：{{ locationAccuracy.toFixed(0) }}米
          </div>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { getPendingTasks,uploadAndCheck} from "../api/index";
import { Toast } from "vant";



export default {
  name: "VehicleInventory",
  data() {
    return {
      searchText: "",
      taskList: [],
      loading: false,
      showCheckDialog: false,
      currentTask: {},
      selectedVideo: null,
      checkRemark: "",
      uploading: false,
      permissions: [],
      // GPS定位信息
      latitude: null,
      longitude: null,
      locationName: "获取中...",
      locationAccuracy: null,
      // 照片相关
      selectedPhotos: [],
    };
  },
  computed: {
    // 检查是否有盘点权限
    canCheck() {
      return this.permissions.includes("vehicle_inventory_check");
    },
    filteredList() {
      if (!this.searchText) return this.taskList;
      const keyword = this.searchText.toLowerCase();
      return this.taskList.filter(
        (item) =>
          (item.vid && item.vid.toLowerCase().includes(keyword)) ||
          (item.ownerCompany &&
            item.ownerCompany.toLowerCase().includes(keyword))
      );
    },
  },
  created() {
    // 从 localStorage 加载权限
    const savedPerms = localStorage.getItem("userPermissions");
    if (savedPerms) {
      this.permissions = JSON.parse(savedPerms);
    }
    this.loadTasks();
  },
  methods: {
    async loadTasks() {
      this.loading = true;
      try {
        const res =await getPendingTasks(); //await uploadAndCheck(formData);
        if (res.code === 0) {
          this.taskList = res.data || [];
        }
      } catch (error) {
        console.error("加载任务失败:", error);
      } finally {
        this.loading = false;
      }
    },
    // 获取GPS定位
    getLocation() {
      if (!navigator.geolocation) {
        this.locationName = "设备不支持定位";
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          this.locationAccuracy = position.coords.accuracy;
          // 逆地理编码获取地名
          this.reverseGeocode(this.latitude, this.longitude);
        },
        (error) => {
          console.error("定位失败:", error);
          this.locationName = "定位失败";
          this.latitude = null;
          this.longitude = null;
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    // 逆地理编码（坐标转地名）
    async reverseGeocode(lat, lng) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=zh-CN`
        );
        const data = await response.json();
        if (data && data.display_name) {
          this.locationName = data.display_name;
        } else {
          // 解析失败，直接用经纬度
          this.locationName = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
      } catch (error) {
        // 网络错误或其他问题，直接用经纬度
        console.error("逆地理编码失败:", error);
        this.locationName = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    },
    startCheck(item) {
      this.currentTask = item;
      this.selectedVideo = null;
      this.checkRemark = "";
      this.latitude = null;
      this.longitude = null;
      this.locationName = "获取中...";
      this.locationAccuracy = null;
      this.showCheckDialog = true;
      // 打开弹窗时获取GPS定位
      this.getLocation();
    },
    onVideoSelected(e) {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 30 * 1024 * 1024) {
          Toast("视频不能超过 30MB");
          return;
        }
        this.selectedVideo = file;
      }
    },
    clearVideo() {
      this.selectedVideo = null;
      this.$refs.videoInput.value = "";
    },
    // 选择照片
    onPhotosSelected(e) {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      // 检查总数限制
      if (this.selectedPhotos.length + files.length > 9) {
        Toast("最多只能选择9张照片");
        return;
      }
      files.forEach(async (file) => {
        if (!file.type.startsWith("image/")) {
          Toast("请选择图片文件");
          return;
        }
        if (file.size > 20 * 1024 * 1024) {
          Toast(`${file.name} 超过20MB限制`);
          return;
        }
        // 压缩照片
        Toast.loading({ message: "压缩中...", forbidClick: true, duration: 0 });
        const compressed = await this.compressImage(file);
        Toast.clear();
        console.log(`压缩: ${file.name} ${(file.size/1024/1024).toFixed(1)}MB → ${(compressed.size/1024/1024).toFixed(1)}MB`);
        const url = URL.createObjectURL(compressed);
        this.selectedPhotos.push({
          file: compressed,
          url,
          name: file.name,
          size: compressed.size,
        });
      });

      // 清空input
      this.$refs.photoInput.value = "";
    },
    // 压缩照片：限制最大边长1920px，质量0.8
    compressImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxSide = 1920;
            let { width, height } = img;

            // 如果图片已经小于限制，降低质量即可
            if (width > maxSide || height > maxSide) {
              if (width > height) {
                height = Math.round((height * maxSide) / width);
                width = maxSide;
              } else {
                width = Math.round((width * maxSide) / height);
                height = maxSide;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  // toBlob失败，用toDataURL兜底
                  const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
                  fetch(dataUrl).then(r => r.blob()).then(b => resolve(b)).catch(() => resolve(file));
                }
              },
              "image/jpeg",
              0.8
            );
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    },
    // 移除照片
    removePhoto(index) {
      URL.revokeObjectURL(this.selectedPhotos[index].url);
      this.selectedPhotos.splice(index, 1);
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    async beforeDialogClose(action, done) {
      if (action === "confirm") {
        //if (!this.selectedVideo) {
        //  Toast("请先选择视频文件");
        // 视频和照片只要有一样就能提交
        if (!this.selectedVideo && this.selectedPhotos.length === 0) {
          Toast("请至少上传视频或照片");
          done(false);
          return;
        }
        if (this.uploading) {
          done(false);
          return;
        }
        await this.submitCheck();
        done(false);
      } else {
        done();
      }
    },
    async submitCheck() {
      this.uploading = true;
      Toast.loading({ message: "上传中...", forbidClick: true, duration: 0 });

      try {
        // 统一使用一个 FormData
        const formData = new FormData();

        // 1. 基础信息
        formData.append("recordId", this.currentTask.no);
        formData.append("remark", this.checkRemark);

        // 2. 坐标信息
        if (this.latitude !== null) formData.append("latitude", this.latitude);
        if (this.longitude !== null)
          formData.append("longitude", this.longitude);
        if (this.locationName)
          formData.append("locationName", this.locationName);
        if (this.locationAccuracy !== null)
          formData.append("locationAccuracy", this.locationAccuracy);

        // 3. 视频
        if (this.selectedVideo) {
          formData.append("video", this.selectedVideo);
        }

        // 4. 照片
        if (this.selectedPhotos.length > 0) {
          this.selectedPhotos.forEach((photo) => {
            formData.append("photos", photo.file);
          });
        }

        // 5. 发起请求（让 Axios 自动处理 Content-Type）
        const res =await uploadAndCheck(formData); //await http.post("/api/Inventory/UploadAndCheck", formData, {timeout: 300000,});
        console.log(res);
        if (res.code !== 0) {
          Toast.fail(res.message || "提交失败");
          return;
        }

        Toast.success("盘点成功！");
        this.showCheckDialog = false;
        this.loadTasks(); // 刷新列表
      } catch (error) {
        Toast.fail("上传失败，请重试");
        console.error("上传失败:", error);
      } finally {
        this.uploading = false;
        Toast.clear(); // 确保在任何情况下都清除 loading
      }
    },
  },
};
</script>

<style scoped>
.inventory {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.search-bar {
  padding: 8px 16px;
  background: #fff;
}

.vehicle-list {
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
}

.vehicle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.vehicle-card:active {
  background: #f8f8f8;
}

.vehicle-info {
  flex: 1;
}

.vehicle-vid {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.vehicle-detail {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.vehicle-company {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.vehicle-location {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.vehicle-action {
  margin-left: 12px;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

/* 盘点弹窗样式 */
.check-content {
  padding: 16px;
}

.check-info {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-row {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  color: #999;
}

.video-upload {
  margin-bottom: 16px;
}

.upload-tip {
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
  text-align: center;
}

.video-preview {
  background: #f0f9ff;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}

.video-name {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.video-size {
  font-size: 12px;
  color: #999;
  margin: 4px 0 8px;
}

/* 定位信息样式 */
.location-info {
  background: #f0f9ff;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.location-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.location-value {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.location-coord,
.location-accuracy {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* 照片上传样式 */
.photo-upload {
  margin-bottom: 12px;
}

.photo-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.photo-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
}

.photo-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.photo-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
}

.photo-count {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  text-align: center;
}
</style>
